import React, { useId } from "react";
import { cn } from "../../lib/cn";

/**
 * Switch - toggle control. On = primary-900 track, off = neutral-200 track.
 * @param {string} [label]
 * @param {boolean} [checked]
 * @param {(checked: boolean) => void} [onChange]
 * @param {boolean} [disabled]
 * @param {string} [className]
 * @param {string} [id]
 */
export const Switch = React.forwardRef(function Switch(
  { label, checked = false, onChange, disabled = false, className, id, ...props },
  ref
) {
  const generatedId = useId();
  const switchId = id || generatedId;

  function toggle() {
    if (disabled) return;
    onChange?.(!checked);
  }

  function handleKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle();
    }
  }

  return (
    <label
      htmlFor={switchId}
      className={cn(
        "inline-flex items-center gap-2 text-sm text-neutral-900 dark:text-neutral-100",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        className
      )}
    >
      <button
        ref={ref}
        type="button"
        id={switchId}
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        className={cn(
          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900",
          checked ? "bg-primary-900" : "bg-neutral-200 dark:bg-neutral-700",
          disabled && "cursor-not-allowed"
        )}
        {...props}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
            checked ? "translate-x-6" : "translate-x-1"
          )}
        />
      </button>
      {label && <span>{label}</span>}
    </label>
  );
});
